import { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'fincenter-a11y-vision';

const DEFAULT = {
  enabled: false,
  fontScale: 1.15,
};

export const VisionAccessibilityContext = createContext(null);

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw);
    return {
      enabled: Boolean(parsed.enabled),
      fontScale: typeof parsed.fontScale === 'number' ? parsed.fontScale : DEFAULT.fontScale,
    };
  } catch {
    return DEFAULT;
  }
}

export function VisionAccessibilityProvider({ children }) {
  const [state, setState] = useState(loadState);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    const root = document.documentElement;
    root.classList.toggle('a11y-vision', state.enabled);
    root.dataset.a11yFont = state.enabled ? String(state.fontScale) : '';
    root.style.setProperty('--a11y-font-scale', state.enabled ? String(state.fontScale) : '1');
    delete root.dataset.a11yTheme;
  }, [state]);

  const setEnabled = (enabled) => setState((s) => ({ ...s, enabled }));
  const increaseFont = () =>
    setState((s) => ({ ...s, fontScale: Math.min(1.6, Math.round((s.fontScale + 0.1) * 10) / 10) }));
  const decreaseFont = () =>
    setState((s) => ({ ...s, fontScale: Math.max(1, Math.round((s.fontScale - 0.1) * 10) / 10) }));
  const resetFont = () => setState((s) => ({ ...s, fontScale: 1.15 }));
  const disable = () => setState(DEFAULT);

  return (
    <VisionAccessibilityContext.Provider
      value={{ ...state, setEnabled, increaseFont, decreaseFont, resetFont, disable }}
    >
      {children}
    </VisionAccessibilityContext.Provider>
  );
}

export function useVisionA11y() {
  const ctx = useContext(VisionAccessibilityContext);
  if (!ctx) throw new Error('useVisionA11y must be used within VisionAccessibilityProvider');
  return ctx;
}
